import "./i18n";
import "./polyfills";

import { theme } from "@eworkit/iris";
import React from "react";
import { createPortal, render } from "react-dom";
import { QueryClient, QueryClientProvider } from "react-query";
import { BrowserRouter } from "react-router-dom";
import { ThemeProvider } from "theme-ui";

import { http, IHttpError } from "@/browser";
import { getOrCreateUnregisteredUserSessionId } from "@/browser/session";
import { AppSuspense } from "@/components/app";
import { onRequest, onSuccessResponse } from "@/data/session";
import { DialogsProvider, MessagesProvider } from "@/eworkitComponents";
import * as PubSub from "@/infrastructure/PubSub";
import { RecaptchaProvider } from "@/infrastructure/Recaptcha";
import { RouteProvider } from "@/infrastructure/Route";
import { SessionProvider } from "@/infrastructure/Session";
import { Pages } from "@/views";
import { Dialogs } from "@/views/components/Dialogs";
import { MaintenanceBanner } from "@/views/components/MaintenanceBanner";
import { Messenger } from "@/views/components/Messenger";
import { OldBrowserInfo } from "@/views/components/OldBrowserInfo";
import { ToastsProvider } from "@/views/components/Toast/ToastsProvider";

import "./app.scss";

import { getVariable, Variables } from "./env";
import i18n from "./i18n";

const FE_VERSION_HEADER = "X-Frontend-Version";
const FE_SESSION_HEADER = "X-Session";
const ACCEPT_LANGUAGE_HEADER = "Accept-Language";

// NOTE: the mobile banner is temporarily hidden
// import "./imb.loader";

const APP_API = getVariable(Variables.APP_API);

if (APP_API) {
  http.defaults.baseURL = APP_API;
}

http.interceptors.request.use(request => {
  if (!request.headers) {
    request.headers = {};
  }

  if (window.__APP_VERSION__?.commit) {
    request.headers[FE_VERSION_HEADER] = window.__APP_VERSION__.commit;
  }

  request.headers[FE_SESSION_HEADER] = getOrCreateUnregisteredUserSessionId();
  if (i18n.language) {
    request.headers[ACCEPT_LANGUAGE_HEADER] = i18n.language.toUpperCase();
  }

  onRequest(request);

  return request;
});

http.interceptors.response.use(
  response => {
    onSuccessResponse(response);
    return response;
  },
  (error: IHttpError) => {
    PubSub.publish("requests.failure", error);
    return Promise.reject(error);
  },
);

const irisTheme = () => ({
  ...theme,
  zIndices: {
    ...theme.zIndices,
    modal: 10000,
    fullScreenModal: 9900,
    fieldDropdown: 10500,
    tooltip: 11000,
    floatingActions: 3500,
  },
});

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: false,
      suspense: true,
      useErrorBoundary: true,
    },
  },
});

function App() {
  return (
    <ThemeProvider theme={irisTheme}>
      <AppSuspense>
        <QueryClientProvider client={queryClient}>
          <RecaptchaProvider>
            <SessionProvider>
              <DialogsProvider>
                <MessagesProvider>
                  <ToastsProvider>
                    {createPortal(<Messenger fixed />, document.body)}
                    <BrowserRouter>
                      <RouteProvider>
                        <OldBrowserInfo />
                        <MaintenanceBanner />
                        <Pages />
                      </RouteProvider>
                    </BrowserRouter>
                    <Dialogs />
                  </ToastsProvider>
                </MessagesProvider>
              </DialogsProvider>
            </SessionProvider>
          </RecaptchaProvider>
        </QueryClientProvider>
      </AppSuspense>
    </ThemeProvider>
  );
}

render(<App />, document.getElementById("root"));
